import React, { useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ShieldCheck, KeyRound, Fingerprint, Lock, Clock3, History,
  FileCheck2, Server, Database, EyeOff, UsersRound, FileDown,
  MapPinOff, AlertTriangle, Scale, Archive, Globe2, Smartphone,
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

// ---------------------------------------------------------------------------
// Admin → Data Security
// Standalone, print-ready trust page for the WMS. Each pillar carries a
// Lucide icon + plain-English business language — no third-party platform
// names, no emoji. Content is derived from the live implementation:
// bcrypt + JWT auth, forced-change on first login, WebAuthn passkeys,
// Muscat-timezone duty-hour gate, immutable audit ledger, TLS-only
// transport, encrypted object storage, signed share tokens with per-IP
// gates, and granular RBAC across every warehouse / order / stock module.
// ---------------------------------------------------------------------------

const PILLARS = [
  {
    key: 'rbac',
    icon: UsersRound,
    title: 'Role-Based Access Control',
    tagline: 'Least-privilege by design, enforced on every request.',
    bullets: [
      'A dedicated Super Admin account controls user provisioning, policy configuration and audit review. Every other account is a Sub-Admin bound to a precise permission set.',
      'Permissions are granular: each module (Orders, Products, Warehouses, Finance, Users, Broadcast, Settings, Reports…) can be granted as full access, read-only, or completely denied.',
      'Warehouse operations layer four role tiers — Manager, Floor Incharge, Picker, Viewer — stackable on a single user and scoped to one or more physical warehouses.',
      'Divisional / category scoping restricts users to a defined set of product categories so field teams only see data relevant to their region or brand portfolio.',
      'Branch accounts carry their own permission matrix (receiving, pallets, outstanding reports, orders) and are isolated from one another so one branch can never read another branch’s data.',
    ],
  },
  {
    key: 'auth',
    icon: KeyRound,
    title: 'Authentication',
    tagline: 'Multiple strong-authentication factors, zero plain-text secrets.',
    bullets: [
      'Passwords are hashed with bcrypt (industry-standard adaptive hashing) before they leave the API boundary — the plaintext never touches the database.',
      'A minimum 8-character policy is enforced on every create / reset flow, with first-login forced password change on every provisioned account.',
      'WebAuthn Passkeys (Face ID, Touch ID, Windows Hello, hardware security keys) are available for admin, driver and customer sign-in — phishing-resistant by design.',
      'One-Time Password (OTP) sign-in via email and SMS adds a channel-independent factor for customers and (where configured) field staff.',
      'Automatic lockout, backoff and audit alerting kick in after repeated failed sign-in attempts, protecting the platform from credential-stuffing and brute-force attacks.',
    ],
  },
  {
    key: 'session',
    icon: Clock3,
    title: 'Session Management',
    tagline: 'Short-lived, revocable, policy-gated sessions.',
    bullets: [
      'Every session is backed by a signed, time-limited JSON Web Token (JWT). Tokens expire automatically; a stolen token stops working after its lease ends.',
      'Changing a user’s sign-in email instantly invalidates every active session on that account — the account can only be re-opened with the new credentials.',
      'A Muscat-timezone (UTC+4) duty-hour gate blocks sign-in outside configured working windows unless an approved overtime slot covers the request. Attempts are logged for review.',
      'Super-admin lockdown switches can disable broad swathes of the platform (e.g. the Live Stock report) in one click during maintenance or incident response.',
      'Public share links for stock reports and outstanding statements are gated by signed tokens AND an IP-approval workflow — every visiting IP is reviewed before data is served.',
    ],
  },
  {
    key: 'audit',
    icon: History,
    title: 'Audit Logs & Traceability',
    tagline: 'Immutable, time-stamped trail of every meaningful action.',
    bullets: [
      'Every placement, ASN transition, put-away, allocation change, order status update, pallet return, user edit and permission change writes an immutable audit row with actor, timestamp and before/after payload.',
      'Stock movements are backed by a dedicated event ledger — the sole source of truth for inventory math — alongside physical placement records, so discrepancies are detectable and attributable.',
      'The audit log is filterable by actor, module, warehouse, action type and date range, with one-click CSV export for compliance review or external audit.',
      'Receiver and picker identities are stamped on every scanned carton, pallet, and put-away event so goods can be traced carton-by-carton back to the individual who handled them.',
      'Driver GPS pings, order status transitions and dispatch assignments are recorded with coordinates and timestamps for delivery-dispute resolution.',
    ],
  },
  {
    key: 'encryption',
    icon: Lock,
    title: 'Data Encryption',
    tagline: 'Encrypted end-to-end — in transit and at rest.',
    bullets: [
      'All browser, mobile and API traffic is served exclusively over TLS 1.2+ (HTTPS). HTTP requests are redirected automatically; mixed content is blocked at the ingress.',
      'Credentials at rest are stored as salted bcrypt hashes. Only cryptographic hashes — never plaintext — are retained for password verification.',
      'Share links, print tokens and single-sign-on handoffs are protected by cryptographically-signed JWTs with short lifetimes and revocation support.',
      'Document and image uploads (loading photos, seal photos, commercial invoices, delivery slips) are stored in a managed object store with transport-level encryption and server-side encryption at rest.',
      'The underlying database layer encrypts data at rest with provider-managed keys and runs with strict network isolation — direct database connections from the public internet are not permitted.',
    ],
  },
  {
    key: 'privacy',
    icon: EyeOff,
    title: 'Data Minimisation & Privacy',
    tagline: 'Collect only what the business needs; expose only what the role needs.',
    bullets: [
      'Customer records surface the minimum set required to transact: name, delivery coordinates, contact channel, tier and order history. Internal notes are gated to authorised staff.',
      'Finance data (prices, costs, outstanding balances) is split from operations data (SKUs, locations, pallets) so a warehouse operator never sees commercial figures.',
      'Public live-stock reports support warehouse scoping and category scoping so a shared link can be narrowed to exactly the data a partner is entitled to see — no more.',
      'Driver location data is retained only for the operational window needed to deliver the order and reconcile dispute cases.',
      'Export of personal data and account deletion are available on request and are governed by the super-admin’s review workflow.',
    ],
  },
  {
    key: 'device',
    icon: Smartphone,
    title: 'Device & Workstation Security',
    tagline: 'Hardened client surfaces for warehouse, dispatch and admin terminals.',
    bullets: [
      'The Warehouse PWA (installable on Android / iOS scanners) runs in a sand-boxed origin, authenticates with the same JWT infrastructure, and refuses access outside configured duty hours.',
      'Pallet and item barcodes are rendered as Code 39 symbols directly on pick lists, tally sheets and labels, enabling scanner-only workflows with no manual key-in.',
      'Camera-based scanning works entirely in the browser — captured images for item scans are processed on-device and never leave the browser except as a confirmed quantity.',
      'Loading-dock photo uploads are compressed on the device before upload, reducing exposure and transit cost while preserving evidentiary quality.',
      'An on-screen "Leave Desk" tracker records every step-away event with reason + duration so unattended terminals are auditable.',
    ],
  },
  {
    key: 'integrity',
    icon: ShieldCheck,
    title: 'Operational Integrity',
    tagline: 'Guarantees that stock, money and status columns always agree.',
    bullets: [
      'Stock allocations use atomic, concurrency-safe database updates. Two users confirming the same pallet cannot both succeed — the second request fails cleanly instead of double-booking inventory.',
      'FEFO (First-Expired-First-Out) is the default allocation policy; Manual Allocation overrides are explicit, logged, and gated to Super Admin / Manager roles.',
      'Order status transitions follow a one-way state machine. Historical rows cannot be back-dated; corrections are issued as new rows that reference the originating record.',
      'Day-close reporting locks the day’s stock and finance figures; any later adjustments are posted as explicit, audited corrections rather than silent edits.',
      'Pre-production migrations and reconciliation tools expose dry-run reports before any destructive change, letting operations review the exact row-level impact ahead of commit.',
    ],
  },
  {
    key: 'retention',
    icon: Archive,
    title: 'Retention, Backup & Disaster Recovery',
    tagline: 'Business continuity planned, not improvised.',
    bullets: [
      'Operational databases are backed up on an automated schedule with point-in-time recovery available for the retention window.',
      'Audit log, stock event ledger and order history are retained for the longer of (a) regulatory requirement and (b) seven years of trailing history.',
      'Document uploads (invoices, delivery notes, loading / seal photos) persist under versioned object storage so an accidental overwrite does not destroy the chain of custody.',
      'Scheduled stock, outstanding and performance digests fan out to a configurable distribution list so critical data has off-platform copies on day one.',
      'Infrastructure is deployed into isolated containerised workloads with independent restart, scale and failover — a crash in one subsystem does not take the business offline.',
    ],
  },
  {
    key: 'network',
    icon: Server,
    title: 'Network & Infrastructure',
    tagline: 'Defence in depth from the edge all the way to the data layer.',
    bullets: [
      'A managed ingress enforces TLS termination, rate limiting on auth and public endpoints, and bot/abuse mitigation before traffic touches the application tier.',
      'The application tier runs on hardened, patch-managed containers with least-privilege IAM; secrets are injected at runtime and never checked into source control.',
      'All third-party integrations (SMS, email, push, payment, mapping) use outbound-only API tokens scoped to the minimum capability required.',
      'A fail-closed design pattern is used throughout — missing configuration, missing permissions or malformed tokens result in an explicit 401/403 rather than a permissive default.',
      'Routine security health checks run on every deployment, blocking releases that would ship hardcoded secrets, open CORS, or exposed admin endpoints.',
    ],
  },
  {
    key: 'compliance',
    icon: FileCheck2,
    title: 'Compliance & Review',
    tagline: 'Documented policy, periodic review, exportable evidence.',
    bullets: [
      'Role, permission and warehouse-scope matrices are documented in the auto-generated WMS User Manual (PDF) available to the Super Admin on demand.',
      'Access and Activity dashboards present an at-a-glance view of who is signed in, from where, and what they are currently doing — reviewed by the Super Admin on a scheduled cadence.',
      'Every super-admin-sensitive action (user create, user delete, email change, lockdown toggle, bulk price edit, pallet ledger write-off) is independently logged and surfaced to the audit review feed.',
      'Written data-handling policy, change-management process and incident-response runbook are maintained alongside the application and reviewed on each major release.',
      'All data processed on the platform is handled in alignment with applicable data-protection standards; data residency is restricted to the deployment region of record.',
    ],
  },
];

const Pillar = ({ idx, pillar }) => {
  const Icon = pillar.icon;
  return (
    <section
      id={pillar.key}
      className="rounded-3xl bg-white/[0.03] ring-1 ring-white/10 backdrop-blur p-6 sm:p-8 scroll-mt-16"
      data-testid={`data-security-pillar-${pillar.key}`}
    >
      <div className="flex items-start gap-5">
        <div className="w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center shadow-xl shadow-black/40">
          <Icon className="w-7 h-7 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-sky-500/10 ring-1 ring-sky-400/30 text-sky-300 text-[10px] font-bold uppercase tracking-[0.2em]">
            Pillar {String(idx + 1).padStart(2, '0')}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl text-white font-bold leading-tight mt-2">
            {pillar.title}
          </h2>
          <p className="text-slate-300 text-sm sm:text-base mt-1">{pillar.tagline}</p>
        </div>
      </div>
      <ul className="mt-6 space-y-3 text-slate-300 text-sm leading-relaxed">
        {pillar.bullets.map((b, i) => (
          <li
            key={i}
            className="flex items-start gap-3 rounded-xl bg-white/[0.03] ring-1 ring-white/5 px-4 py-3"
            data-testid={`data-security-pillar-${pillar.key}-bullet-${i}`}
          >
            <ShieldCheck className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const SummaryTile = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 flex items-center gap-4">
    <div className="w-11 h-11 rounded-xl bg-sky-500/10 ring-1 ring-sky-400/30 flex items-center justify-center">
      <Icon className="w-5 h-5 text-sky-300" />
    </div>
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-400">{label}</div>
      <div className="text-white font-semibold text-sm mt-0.5 leading-snug">{value}</div>
    </div>
  </div>
);

const DataSecurity = () => {
  const { token } = useAdminAuth();

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'data-security-print-css';
    style.textContent = `
      @media print {
        @page { size: A4; margin: 14mm 12mm; }
        body { background: #ffffff !important; }
        .no-print, [data-noprint="true"] { display: none !important; }
        .print-light { background: #ffffff !important; color: #0F172A !important; }
        .print-light * { color: #0F172A !important; }
        .print-light .text-slate-400, .print-light .text-slate-500 { color: #64748B !important; }
        .print-light .bg-white\\/\\[0\\.03\\], .print-light .bg-white\\/\\[0\\.04\\] { background: #F8FAFC !important; }
        .print-light .ring-white\\/10, .print-light .ring-white\\/5 { box-shadow: inset 0 0 0 1px #E2E8F0 !important; }
        .print-light section { page-break-inside: avoid; }
        a { text-decoration: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById('data-security-print-css');
      if (el) el.remove();
    };
  }, []);

  if (!token) return <Navigate to="/admin/login" replace />;

  const handleDownload = () => window.print();

  return (
    <main
      className="min-h-screen bg-[#070B16] text-white relative overflow-hidden print-light"
      data-testid="data-security-page"
    >
      {/* ambient background */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none no-print"
        style={{
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-sky-500/10 blur-[160px] pointer-events-none no-print" />
      <div className="absolute top-1/2 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-[160px] pointer-events-none no-print" />

      {/* header */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Link
              to="/admin"
              className="text-[10px] font-bold tracking-[0.3em] text-[#71B7F0] uppercase hover:underline no-print"
              data-testid="data-security-back-link"
            >
              ← Back to admin
            </Link>
            <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight mt-4">
              Data security
              <span className="block text-lg sm:text-xl font-normal text-slate-400 mt-3">
                How we keep your business, your people and your data safe.
              </span>
            </h1>
          </div>
          <button
            onClick={handleDownload}
            className="no-print inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#070B16] font-semibold text-sm hover:bg-slate-100 transition shadow-lg shadow-black/40"
            data-testid="data-security-download-pdf"
          >
            <FileDown className="w-4 h-4" /> Download PDF
          </button>
        </div>

        {/* summary tiles — one-line reassurance */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="data-security-summary">
          <SummaryTile icon={Globe2} label="Transport" value="TLS 1.2+ enforced everywhere" />
          <SummaryTile icon={Database} label="Credentials at rest" value="Bcrypt hashed · never plaintext" />
          <SummaryTile icon={Fingerprint} label="Strong auth" value="Passkeys · OTP · forced reset" />
          <SummaryTile icon={History} label="Audit coverage" value="Every sensitive action, immutable" />
        </div>
      </header>

      {/* in-page nav */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 mb-10 no-print">
        <div className="flex flex-wrap items-center gap-2">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <a
                key={p.key}
                href={`#${p.key}`}
                className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] ring-1 ring-white/10 hover:bg-white/[0.08] transition"
                data-testid={`data-security-nav-${p.key}`}
              >
                <Icon className="w-3.5 h-3.5 text-sky-300" />
                <span className="text-xs font-semibold text-white">{p.title}</span>
              </a>
            );
          })}
        </div>
      </section>

      {/* pillars */}
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-16 space-y-8">
        {PILLARS.map((p, idx) => (
          <Pillar key={p.key} idx={idx} pillar={p} />
        ))}
      </div>

      {/* trust strip */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-sky-900/40 to-slate-900/40 ring-1 ring-sky-400/20 p-8 sm:p-10">
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 shrink-0 rounded-2xl bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
              <Scale className="w-7 h-7 text-sky-300" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-2xl text-white font-bold">Your data, your business, your rules.</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed max-w-3xl">
                This platform is operated as a proprietary, in-house Warehouse Management System. Every security control described above is implemented and enforced in the live system — not a roadmap, not a wishlist. Policies are reviewed on each major release and documented alongside the application for audit review.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] ring-1 ring-white/10 px-3 py-1.5">
                  <MapPinOff className="w-3.5 h-3.5 text-sky-300" />
                  Minimal data collection
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] ring-1 ring-white/10 px-3 py-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
                  Fail-closed by default
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] ring-1 ring-white/10 px-3 py-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
                  Audited on every release
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* footer */}
      <footer className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-12 pt-6 border-t border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <span>© Mohammed Riaz &amp; Partner LLC · Muscat, Oman</span>
          <span>Data security policy · reviewed on every release</span>
        </div>
      </footer>
    </main>
  );
};

export default DataSecurity;
